/**
 * Панель деталей заявки в менеджерском канбане (#34, manager v3 §6.2).
 *
 * Композиция РОВНО по дизайн-макету §6.2 «Детали заявки + chat»:
 *   topbar  — крошки «Заявки / #N» + заголовок + чип статуса.
 *   grid-2  — слева stack карточек, справа карточка чата.
 *     ЛЕВО:
 *       • Timeline       — вертикальная лента событий (создано → назначено →
 *                          комментарии → текущий статус + остаток SLA →
 *                          выполнено). Собирается из доступных timestamp'ов
 *                          заявки (created_at / updated_at / resolved_at /
 *                          sla_due_at) + комментариев-событий. Журнала смен
 *                          статуса с временами на бэке нет — лента честно
 *                          опирается на то, что есть.
 *       • Запрос         — текст payload (что просили) + Гость · Номер · Тип;
 *                          фото-вложения «до» (из комментариев с attachments).
 *       • Исполнитель    — аватар (инициалы) + имя + отдел/должность + загрузка
 *                          + кнопка «Сменить исполнителя» (раскрывает дропдаун
 *                          ПРАВОМОЧНЫХ — useAssignableStaff, §6.2 п.3).
 *       • Статус         — кнопки force-status (СОХРАНены — смена статуса).
 *     ПРАВО:
 *       • Chat           — бабблы (гость слева, staff/менеджер справа) + ввод.
 *
 * Реализовано через текущую дизайн-систему tenant-app (классы hsc-card /
 * rqk-* / rq-chat + токены --hsc-*), а не инлайн-стили макета.
 *
 * Кнопка «‹ Назад к доске» остаётся: деталь — отдельный экран, не модалка
 * (feedback_every_form_is_a_separate_screen).
 *
 * i18n: все строки RU; сырые англ-коды state/категории мапятся через
 * kanban-model (stateLabel/categoryLabel/priorityLabel).
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { ManagerRequest } from '@hsc/types';
import {
  COLUMNS,
  assigneeLabel,
  categoryLabel,
  columnOf,
  guestLabel,
  priorityLabel,
  requestNumber,
  slaInfo,
  stateLabel,
  stayLabel,
  type ColumnKey,
} from './kanban-model.js';
import {
  useAssignRequest,
  useAssignableStaff,
  useKanbanAddComment,
  useKanbanRequestDetail,
} from './useKanbanRequestDetail.js';

/** Дата-время «20.05.2026, 14:30». */
function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Только время «14:30» — для коротких подписей в ленте/чате. */
function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Инициалы для аватара по отображаемому имени («Лена Власова» → «ЛВ»). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export interface RequestDetailPanelProps {
  /** Заявка из канбан-списка (для мгновенного рендера до загрузки деталей). */
  req: ManagerRequest;
  /** Закрыть панель — «Назад к доске». */
  onBack: () => void;
  /** Сменить статус заявки на целевую колонку (force-status). */
  onMove: (to: ColumnKey) => void;
  /** Идёт мутация смены статуса. */
  isMoving: boolean;
}

/** Панель/форма деталей заявки канбана (§6.2). */
export function RequestDetailPanel({
  req,
  onBack,
  onMove,
  isMoving,
}: RequestDetailPanelProps): JSX.Element {
  const { data, isLoading } = useKanbanRequestDetail(req.id);
  const addComment = useKanbanAddComment(req.id);
  const assign = useAssignRequest(req.id);
  const assignable = useAssignableStaff(req.id);
  const [comment, setComment] = useState('');
  const [pickedStaff, setPickedStaff] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);

  // Деталь приходит с бэка; до загрузки показываем то, что есть в карточке.
  const rq = data?.request ?? req;
  const comments = useMemo(() => data?.comments ?? [], [data?.comments]);
  const merged = useMemo(() => ({ ...req, ...rq }) as ManagerRequest, [req, rq]);
  const sla = slaInfo(merged);
  const curCol = columnOf(rq.state);
  const assigneeName = assigneeLabel(merged);
  const hasAssignee = Boolean(merged.assigned_staff_id);

  // Фото «до» — вложения комментариев с presigned URL (#45).
  const photos = useMemo(() => {
    const urls: Array<{ url: string; key: string }> = [];
    for (const c of comments) {
      for (const a of c.attachments ?? []) {
        if (a.url) urls.push({ url: a.url, key: a.key });
      }
    }
    return urls;
  }, [comments]);

  // Лента событий (§6.2 Timeline): только из доступных данных.
  const timeline = useMemo(() => {
    const ev: Array<{ at: string | undefined; text: string }> = [];
    ev.push({ at: rq.created_at, text: `Заявка создана${guestLabel(merged) ? ` · ${guestLabel(merged)}` : ''}` });
    if (hasAssignee) {
      ev.push({ at: undefined, text: `Назначен исполнитель · ${assigneeName}` });
    }
    for (const c of comments) {
      const att = (c.attachments ?? []).length;
      ev.push({ at: c.created_at, text: att > 0 ? `Сообщение + ${att} фото` : 'Сообщение в чате' });
    }
    if (rq.resolved_at) {
      ev.push({ at: rq.resolved_at, text: 'Заявка выполнена' });
    } else {
      ev.push({ at: undefined, text: `Сейчас: ${stateLabel(rq.state)} · SLA ${sla.label}` });
    }
    return ev;
  }, [rq.created_at, rq.resolved_at, rq.state, hasAssignee, assigneeName, comments, merged, sla.label]);

  // Кандидаты в исполнители — ТОЛЬКО правомочные (отдел категории +
  // guest_services), приходят с GET /v1/requests/{id}/assignable-staff.
  const staffOptions = assignable.data ?? [];

  function submitComment(): void {
    const body = comment.trim();
    if (body === '') return;
    addComment.mutate(body, { onSuccess: () => setComment('') });
  }

  function submitAssign(): void {
    if (pickedStaff === '') return;
    assign.mutate(pickedStaff, {
      onSuccess: () => {
        setPickedStaff('');
        setReassignOpen(false);
      },
    });
  }

  return (
    <section data-testid="kanban-detail-panel">
      <button type="button" className="ta-back" data-testid="kanban-detail-back" onClick={onBack}>
        ‹ Назад к доске
      </button>

      {/* topbar §6.2 — крошки + заголовок + чип статуса. */}
      <div className="rqk-detail__topbar">
        <div>
          <div className="rqk-detail__crumbs">Заявки / {requestNumber(merged)}</div>
          <h1 className="hsc-page__title" data-testid="kanban-detail-id">
            {rq.title}
          </h1>
        </div>
        <span className="rqk-chip" data-testid="kanban-detail-status">
          {stateLabel(rq.state)}
        </span>
      </div>

      <div className="rqk-detail__grid2">
        {/* ЛЕВО — stack карточек. */}
        <div className="rqk-detail__stack">
          {/* Timeline. */}
          <Card>
            <h2 className="hsc-card__title">Timeline</h2>
            <ol className="rqk-timeline" data-testid="kanban-detail-timeline">
              {timeline.map((e, i) => (
                <li key={i} className="rqk-timeline__item">
                  <span className="rqk-timeline__time">{e.at ? fmtTime(e.at) : '—'}</span>
                  <span className="rqk-timeline__text">{e.text}</span>
                </li>
              ))}
            </ol>
          </Card>

          {/* Запрос. */}
          <Card>
            <h2 className="hsc-card__title">Запрос</h2>
            {rq.description !== undefined && rq.description !== '' ? (
              <p className="rqk-detail__desc" data-testid="kanban-detail-desc">
                {rq.description}
              </p>
            ) : (
              <p className="rqk-detail__desc" data-testid="kanban-detail-desc">
                {rq.title}
              </p>
            )}
            <p className="rqk-detail__meta">
              Гость: {guestLabel(merged) || '—'} · Номер:{' '}
              {stayLabel(merged) || (merged.stay_id ? 'Проживание' : '—')} · Тип:{' '}
              {categoryLabel(rq.category)} · {priorityLabel(rq.priority)}
            </p>
            {photos.length > 0 && (
              <>
                <div className="rqk-detail__photos" data-testid="kanban-detail-photos">
                  {photos.map((p) => (
                    <a
                      key={p.key}
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rqk-detail__photo-link"
                    >
                      <img src={p.url} alt="Фото к заявке" className="rqk-detail__photo" />
                    </a>
                  ))}
                </div>
                <p className="rqk-detail__photos-cap">{photos.length} фото «до»</p>
              </>
            )}
          </Card>

          {/* Исполнитель. */}
          <Card>
            <h2 className="hsc-card__title">Исполнитель</h2>
            <div className="rqk-assignee" data-testid="kanban-detail-assignee">
              <div className="rqk-avatar" aria-hidden="true">
                {hasAssignee ? initials(assigneeName) : '—'}
              </div>
              <div>
                <div className="rqk-assignee__name" data-testid="kanban-detail-assignee-current">
                  {hasAssignee ? assigneeName : 'Не назначен'}
                </div>
                <div className="rqk-assignee__sub">
                  {hasAssignee ? 'Назначен на заявку' : 'Выберите сотрудника из правомочных'}
                </div>
              </div>
            </div>

            {!reassignOpen ? (
              <div className="rq-actions">
                <button
                  type="button"
                  className="rqk-act rqk-act--ghost"
                  data-testid="kanban-detail-reassign-open"
                  onClick={() => setReassignOpen(true)}
                >
                  Сменить исполнителя
                </button>
              </div>
            ) : (
              <div className="ta-field rqk-assign-form">
                <label className="hsc-input__label" htmlFor="kanban-detail-assign-select">
                  Назначить сотрудника
                </label>
                {assignable.isLoading ? (
                  <Skeleton height={40} radius={10} />
                ) : assignable.isError ? (
                  <p role="alert" className="ta-muted">
                    Не удалось загрузить список исполнителей.
                  </p>
                ) : staffOptions.length === 0 ? (
                  <p className="ta-muted" data-testid="kanban-detail-assign-empty">
                    Нет доступных исполнителей для этого типа заявки.
                  </p>
                ) : (
                  <div className="rqk-assign-row">
                    <select
                      id="kanban-detail-assign-select"
                      className="ta-select"
                      data-testid="kanban-detail-assign-select"
                      value={pickedStaff}
                      onChange={(e) => setPickedStaff(e.target.value)}
                    >
                      <option value="">— выберите сотрудника —</option>
                      {staffOptions.map((o) => (
                        <option key={o.user_id} value={o.user_id}>
                          {o.name}
                          {o.position_title ? ` · ${o.position_title}` : ''}
                          {o.department_name ? ` · ${o.department_name}` : ''}
                          {` · ${o.open_load} в работе`}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      loading={assign.isPending}
                      disabled={pickedStaff === ''}
                      data-testid="kanban-detail-assign-submit"
                      onClick={submitAssign}
                    >
                      Назначить
                    </Button>
                  </div>
                )}
                {assign.isError && (
                  <p role="alert" className="ta-error" data-testid="kanban-detail-assign-error">
                    Не удалось назначить исполнителя. Попробуйте ещё раз.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Статус — смена статуса заявки (force-status, СОХРАНены). */}
          <Card>
            <h2 className="hsc-card__title">Статус</h2>
            <div className="rqk-detail__actions" data-testid="kanban-detail-actions">
              {COLUMNS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`rqk-act${c.key === curCol ? ' rqk-act--current' : ''}${
                    c.key === 'cancelled' ? ' rqk-act--ghost' : ''
                  }`}
                  disabled={isMoving || c.key === curCol}
                  data-testid={`kanban-detail-move-${c.key}`}
                  onClick={() => onMove(c.key)}
                >
                  {c.title}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* ПРАВО — чат. */}
        <section
          className="rqk-detail__chat"
          data-testid="kanban-detail-chat"
          aria-label="Переписка по заявке"
        >
          <h2 className="hsc-card__title">Chat</h2>
          <div className="rqk-detail__chat-body">
            {isLoading ? (
              <Skeleton height={64} radius={12} />
            ) : comments.length === 0 ? (
              <p className="ta-muted">Сообщений пока нет.</p>
            ) : (
              <ul className="rq-msgs">
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className="rq-msg rq-msg--them"
                    data-testid={`kanban-detail-msg-${c.id}`}
                  >
                    <span className="rq-msg__body">{c.body}</span>
                    <span className="rq-msg__time">{fmtDateTime(c.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rq-chat-form">
            <label className="hsc-input__label" htmlFor="kanban-detail-comment">
              Новое сообщение
            </label>
            <textarea
              id="kanban-detail-comment"
              className="ta-textarea"
              rows={2}
              placeholder="Сообщение…"
              data-testid="kanban-detail-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="rq-actions">
              <Button
                type="button"
                loading={addComment.isPending}
                disabled={comment.trim() === ''}
                data-testid="kanban-detail-comment-send"
                onClick={submitComment}
              >
                Отправить
              </Button>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}
